/**
 * Composable для работы с уведомлениями
 * Централизует логику показа уведомлений об ошибках и успешных операциях
 */

import { ErrorHandler } from '../services/errorHandler'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

/**
 * Создает composable для работы с уведомлениями
 *
 * @example
 * ```ts
 * const { showError, showSuccess, showInfo } = useNotifications()
 *
 * try {
 *   await someOperation()
 *   showSuccess('Операция выполнена успешно')
 * } catch (error) {
 *   showError(error, 'Some operation')
 * }
 * ```
 */
export const useNotifications = () => {
  const feedbackStore = useFeedbackStore()

  /**
   * Показывает уведомление об ошибке
   * Автоматически обрабатывает ошибку через ErrorHandler
   */
  const showError = (error: unknown, context: string): void => {
    const errorInfo = ErrorHandler.handle(error, context)
    feedbackStore.showToast({
      type: 'error',
      message: errorInfo.message,
      time: 7000,
    })
  }

  /**
   * Показывает уведомление об успешной операции
   */
  const showSuccess = (message: string): void => {
    feedbackStore.showToast({
      type: 'success',
      message,
      time: 3000,
    })
  }

  /**
   * Показывает информационное уведомление
   */
  const showInfo = (message: string): void => {
    feedbackStore.showToast({
      type: 'info',
      message,
      time: 3000,
    })
  }

  /**
   * Показывает предупреждение
   */
  const showWarning = (message: string): void => {
    feedbackStore.showToast({
      type: 'warning',
      message,
      time: 3000,
    })
  }

  return {
    showError,
    showSuccess,
    showInfo,
    showWarning,
  }
}
